import { useCallback, useMemo } from 'react';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import filterHiddenStyles from '../components/LayerMenu/filterHiddenStyles';
import { baseMapStyles, conditionsStyles, heatmapStyles, layerOverlayStyles, terrainOverlayStyles } from '../components/LayerMenu/layerMenuStyles';
import { RootState, useAppSelector } from '../redux';
import { StyleId } from '../types/Styles';

type UseLayerMenuStylesArgs = {
  allowedStyleIds?: StyleId[];
  /** Opt-in for surfaces that never expose admin/PLP layers, even to an admin. */
  hideAdminStyles?: boolean;
};

/**
 * The layer menu's data layer, without any UI. Resolves the menu's style groupings against the
 * viewer's permissions, the map's supported overlays, and the current 2D/3D mode, so a surface can
 * render its own menu without re-deriving any of it.
 */
const useLayerMenuStyles = ({ allowedStyleIds, hideAdminStyles }: UseLayerMenuStylesArgs = {}) => {
  const { hasPermission } = useAuthorization();
  const isAdmin = !hideAdminStyles && hasPermission({ permission: 'trails:manage' });
  const isPLP = !hideAdminStyles && hasPermission({ permission: 'public_lands:manage' });
  const { is3dActive, supportedDynamicStyleIds } = useAppSelector((state: RootState) => ({
    is3dActive: state.map.is3dActive,
    supportedDynamicStyleIds: state.map.supportedDynamicStyleIds
  }));

  const filterVisibleStyles = useCallback(
    (ids: StyleId[]) => filterHiddenStyles({ allowedStyleIds, ids, isAdmin, isPLP, is3dActive, supportedDynamicStyleIds }),
    [allowedStyleIds, is3dActive, isAdmin, isPLP, supportedDynamicStyleIds]
  );

  const baseMapIds = useMemo(() => filterVisibleStyles(baseMapStyles), [filterVisibleStyles]);
  const conditionsIds = useMemo(() => filterVisibleStyles(conditionsStyles), [filterVisibleStyles]);
  const heatmapIds = useMemo(() => filterVisibleStyles(heatmapStyles), [filterVisibleStyles]);
  const layerOverlayIds = useMemo(() => filterVisibleStyles(layerOverlayStyles), [filterVisibleStyles]);
  const terrainIds = useMemo(() => filterVisibleStyles(terrainOverlayStyles), [filterVisibleStyles]);

  return useMemo(
    () => ({ baseMapIds, conditionsIds, filterVisibleStyles, heatmapIds, isAdmin, isPLP, layerOverlayIds, terrainIds }),
    [baseMapIds, conditionsIds, filterVisibleStyles, heatmapIds, isAdmin, isPLP, layerOverlayIds, terrainIds]
  );
};

export default useLayerMenuStyles;
